import { useEffect, useRef, useCallback } from 'react';
import { css } from '@emotion/react';
import { AnimatePresence } from 'motion/react';
import { getUser, PortalUser } from '@frontend/auth-helpers';
import { ErrorBoundary, MainBoundary } from '@frontend/error-boundary';
import { SurfaceHeader } from '@frontend/surface-header';
import { theme } from '@frontend/theme';
import { useModalControl, Tray, getAllFocusableElements } from '@frontend/design-system';
import { DecoratedLocationPicker } from '../location-picker';
import { Profile } from './components/profile/profile';
import {
  dispatchTopBarModalClose,
  dispatchTopBarModalOpen,
  TOP_BAR_MODAL_EVENT,
  TopBarModalEventDetail,
} from './events/modal-events';
import { PaddedContent } from './layout/padded-content';
import { useTopBarShallowStore } from './store/use-top-bar-store';
import { restoreFocusForIcon } from './utils/focus-registry';
import { labelMap } from './utils/label-map';

export const TopBarTray = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  const { modalProps, openModal, closeModal } = useModalControl({ disableReturnFocus: true });
  const { selectedIcon, setSelectedIcon } = useTopBarShallowStore('selectedIcon', 'setSelectedIcon');

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<TopBarModalEventDetail>).detail;
      if (!detail) return;
      if (detail.action === 'open') {
        if (!modalProps.show) openModal();
      } else if (detail.action === 'close') {
        if (modalProps.show) closeModal();
        setSelectedIcon('');
        restoreFocusForIcon(selectedIcon);
      }
    };
    window.addEventListener(TOP_BAR_MODAL_EVENT, handler as EventListener);
    return () => window.removeEventListener(TOP_BAR_MODAL_EVENT, handler as EventListener);
  }, [modalProps.show, selectedIcon, closeModal, openModal, setSelectedIcon]);

  useEffect(() => {
    let focusableElements: HTMLElement[];
    if (containerRef.current && modalProps.show) {
      focusableElements = getAllFocusableElements(containerRef.current) as HTMLElement[];

      if (focusableElements.length) {
        focusableElements[0].focus();
      }
    }
  }, [modalProps.show, selectedIcon]);

  return (
    <AnimatePresence>
      <Tray
        mountTarget='#app-container'
        width='medium'
        autoFocusTimeout={3600000 * 2}
        css={{ padding: 0, borderTop: `1px solid ${theme.colors.neutral20}` }}
        {...modalProps}
        onClose={() => {
          modalProps.onClose?.();
          setSelectedIcon('');
        }}
      >
        <ErrorBoundary tier='feature' feature='top-bar-tray' team='fe-ux-platform' fallback={MainBoundary}>
          <TopBarTrayHeader />
          <div
            ref={containerRef}
            css={css`
              display: flex;
              flex-direction: column;
              position: relative;
              overflow: auto;
              flex-grow: 1;
            `}
          >
            <LocationTrayContent />
            <ProfileTrayContent />
          </div>
        </ErrorBoundary>
      </Tray>
    </AnimatePresence>
  );
};

const TopBarTrayHeader = () => {
  const { selectedIcon } = useTopBarShallowStore('selectedIcon');

  return (
    selectedIcon !== 'chat' && (
      <SurfaceHeader.Root noBorder css={{ paddingBottom: 0, marginBottom: `-${theme.spacing(1)}` }}>
        <SurfaceHeader.Row>
          <SurfaceHeader.Title data-testid='tray-header'>
            {selectedIcon !== 'location' && selectedIcon && labelMap[selectedIcon]}
            {selectedIcon === 'location' && labelMap['multiLocations']}
          </SurfaceHeader.Title>
          <SurfaceHeader.Actions>
            <SurfaceHeader.Icon iconName='x' onClick={dispatchTopBarModalClose} />
          </SurfaceHeader.Actions>
        </SurfaceHeader.Row>
      </SurfaceHeader.Root>
    )
  );
};

const LocationTrayContent = () => {
  const { selectedIcon } = useTopBarShallowStore('selectedIcon');

  return (
    selectedIcon === 'location' && (
      <PaddedContent css={{ height: '100%', padding: 0 }}>
        <DecoratedLocationPicker closeModal={dispatchTopBarModalClose} />
      </PaddedContent>
    )
  );
};

const ProfileTrayContent = () => {
  const user = getUser();
  const { selectedIcon, setSelectedIcon, nextVersion } = useTopBarShallowStore(
    'selectedIcon',
    'setSelectedIcon',
    'nextVersion'
  );

  const handleOpenProfilePanel = useCallback(() => {
    setSelectedIcon('profile');
    dispatchTopBarModalOpen();
  }, [setSelectedIcon]);

  return (
    selectedIcon === 'profile' && (
      <Profile
        user={user as PortalUser}
        closeModal={dispatchTopBarModalClose}
        nextVersion={nextVersion}
        handleOpenProfilePanel={handleOpenProfilePanel}
      />
    )
  );
};
